import { useState } from 'react';

// third-party
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import OtpInput from 'react-otp-input';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// project import
import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import useAuth from 'hooks/useAuth';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { isValidPassword } from 'utils/validators';
import { OTP_CONTAINER_CLASS, OTP_INPUT_CLASS } from './otpInputClasses';
import { PasswordInput } from './PasswordInput';

// ============================|| STATIC - CODE VERIFICATION ||============================ //
interface Props {
    email: string | null;
    onSendResetCode: () => void;
    missingEmailError: () => void;
}

interface CodeVerificationValues {
    password: string;
    password_again: string;
    otp: string;
}

const OTP_LENGTH = 6;

const AuthCodeVerification = ({ email, onSendResetCode, missingEmailError }: Props) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { resetPassword } = useAuth();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors, isSubmitting },
    } = useForm<CodeVerificationValues>({
        mode: 'onBlur',
        defaultValues: { password: '', password_again: '', otp: '' },
    });

    const otpValue = useWatch({ control, name: 'otp' }) ?? '';

    const onSubmit = async (values: CodeVerificationValues) => {
        setSubmitError(null);
        try {
            if (!email) {
                missingEmailError();
                return;
            }
            const res = await resetPassword(email, values.otp, values.password);

            if (res.toLowerCase() === 'success') {
                successAlert(t('auth.password-reset-successfully'));

                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 1500);
            }
        } catch (err) {
            console.error(err);
            if (err instanceof Error) {
                setSubmitError(err.message);
            }
        }
    };

    const passwordRegister = register('password', {
        required: t('auth.password-required'),
        validate: async (value) => {
            const res = await isValidPassword(value, t('auth.invalid-password')).catch(() => null);
            return res?.isValid ? true : t('auth.password-requirements');
        },
    });
    const passwordAgainRegister = register('password_again', {
        required: t('auth.password-required'),
        validate: (value) => value === getValues('password') || t('auth.passwords-must-match'),
    });

    const preventCopy = (e: React.ClipboardEvent) => {
        e.preventDefault();
    };

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
            noValidate
        >
            <div className="flex flex-col gap-6">
                <div>
                    <Controller
                        name="otp"
                        control={control}
                        rules={{
                            required: t('auth.verification-code-required'),
                            validate: (value) =>
                                value.length === OTP_LENGTH ||
                                t('auth.verification-code-length', { OTP_LENGTH: OTP_LENGTH.toString() }),
                        }}
                        render={({ field }) => (
                            <OtpInput
                                value={field.value}
                                onChange={field.onChange}
                                numInputs={OTP_LENGTH}
                                containerStyle={OTP_CONTAINER_CLASS}
                                inputStyle={OTP_INPUT_CLASS}
                                skipDefaultStyles
                                renderInput={(inputProps) => <input {...inputProps} />}
                            />
                        )}
                    />
                    {errors.otp && <FieldError>{errors.otp.message}</FieldError>}
                </div>
                {otpValue.length === OTP_LENGTH && (
                    <>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="password-login">
                                <FormattedMessage id="auth.password" />
                            </Label>
                            <PasswordInput
                                id="password-login"
                                placeholder={t('auth.enter-password')}
                                aria-invalid={Boolean(errors.password)}
                                visible={showPassword}
                                onToggleVisible={() => setShowPassword(!showPassword)}
                                onCopy={preventCopy}
                                {...passwordRegister}
                            />
                            {errors.password && <FieldError>{errors.password.message}</FieldError>}
                            <Input
                                id="password-login-again"
                                aria-label={t('auth.enter-password-again')}
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t('auth.enter-password-again')}
                                aria-invalid={Boolean(errors.password_again)}
                                onCopy={preventCopy}
                                {...passwordAgainRegister}
                            />
                            {errors.password_again && <FieldError>{errors.password_again.message}</FieldError>}
                        </div>
                        <Button
                            {...buttonStyle({ variant: 'contained' }, 'w-full')}
                            type="submit"
                            disabled={isSubmitting}
                        >
                            <FormattedMessage id="continue" />
                        </Button>
                    </>
                )}
                {submitError && <FieldError>{submitError}</FieldError>}
                <p className="text-body1 m-0">
                    <FormattedMessage id="auth.did-not-receive-code" />{' '}
                    <button
                        type="button"
                        className="text-primary cursor-pointer"
                        onClick={() => {
                            setValue('otp', '');
                            onSendResetCode();
                        }}
                    >
                        <FormattedMessage id="auth.resend-code" />
                    </button>
                </p>
                <RouterLink to="/login" className="text-fg hover:text-link w-fit no-underline hover:underline">
                    <FormattedMessage id="auth.sign-in" />
                </RouterLink>
            </div>
        </form>
    );
};

export default AuthCodeVerification;
